import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Search, X, Check } from 'lucide-react'
import { api } from '../services/api'
import Input from './Input'

export default function FormSelector({ value, onChange, readOnly = false }) {
  const [showDropdown, setShowDropdown] = useState(false)
  const [searchQuery, setSearchQuery] = useState('')

  const { data: formsRes, isLoading } = useQuery({
    queryKey: ['forms', 'selector', searchQuery],
    queryFn: () =>
      api
        .get('/forms', { params: { search: searchQuery, active: true } })
        .then((r) => r.data),
    enabled: showDropdown,
  })

  const forms = formsRes?.data || []

  const selectedForm = value
    ? forms.find((f) => f.id === value?.id) || value
    : null

  const handleSelect = (form) => {
    if (readOnly) return
    onChange(form)
    setShowDropdown(false)
  }

  const handleClear = () => {
    if (readOnly) return
    onChange(null)
  }

  if (readOnly) {
    return (
      <div className="text-sm text-gray-900">
        {selectedForm ? selectedForm.name : 'No form selected'}
      </div>
    )
  }

  return (
    <div className="space-y-2">
      <div className="relative">
        <div
          className="flex items-center space-x-2 border border-gray-300 rounded-md px-3 py-2 cursor-pointer bg-white"
          onClick={() => setShowDropdown(!showDropdown)}
        >
          <Search className="h-4 w-4 text-gray-400" />
          <span className="flex-1 text-sm text-gray-600">
            {selectedForm ? selectedForm.name : 'Click to select a form...'}
          </span>
          {selectedForm && (
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation()
                handleClear()
              }}
              className="text-gray-400 hover:text-gray-600"
            >
              <X className="h-4 w-4" />
            </button>
          )}
        </div>

        {showDropdown && (
          <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-64 overflow-hidden">
            <div className="p-2 border-b">
              <Input
                placeholder="Search forms..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                autoFocus
              />
            </div>
            <div className="max-h-48 overflow-y-auto">
              {isLoading ? (
                <div className="p-4 text-center text-sm text-gray-500">
                  Loading...
                </div>
              ) : forms.length === 0 ? (
                <div className="p-4 text-center text-sm text-gray-500">
                  No forms found
                </div>
              ) : (
                forms.map((form) => {
                  const isSelected = selectedForm?.id === form.id
                  return (
                    <div
                      key={form.id}
                      onClick={() => handleSelect(form)}
                      className="flex items-center justify-between px-3 py-2 hover:bg-gray-50 cursor-pointer border-b border-gray-100 last:border-b-0"
                    >
                      <div>
                        <span className="text-sm text-gray-900">
                          {form.name}
                        </span>
                        <span className="ml-2 text-xs text-gray-400">
                          ({form.slug})
                        </span>
                      </div>
                      {isSelected && (
                        <Check className="h-4 w-4 text-primary-600" />
                      )}
                    </div>
                  )
                })
              )}
            </div>
          </div>
        )}
      </div>

      {selectedForm && (
        <div className="inline-flex items-center px-3 py-1 rounded-full text-sm bg-primary-100 text-primary-800">
          <span>{selectedForm.name}</span>
          <button
            type="button"
            onClick={handleClear}
            className="ml-2 text-primary-600 hover:text-primary-800"
          >
            <X className="h-3 w-3" />
          </button>
        </div>
      )}
    </div>
  )
}

